import { useState, useEffect } from 'react'
import { Tabs, Button, Toast, Tag, Empty, Loading, Field } from 'react-vant'
import { ArrowLeft, ClockO, LocationO, Scan, Arrow } from '@react-vant/icons'
import { useNavigate, useSearchParams } from 'react-router-dom'
import request from '../../api/request'

export default function RefereeLogin() {
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const urlCode = searchParams.get('code')

    const [loading, setLoading] = useState(false)
    const [code, setCode] = useState((urlCode && urlCode !== 'null') ? urlCode : '')
    const [schedules, setSchedules] = useState([])
    const [activeTab, setActiveTab] = useState(0)

    useEffect(() => {
        if (urlCode && urlCode !== 'null') {
            handleVerify(urlCode)
        }
    }, [urlCode])

    const handleVerify = async (manualCode) => {
        const targetCode = typeof manualCode === 'string' ? manualCode : code
        if (!targetCode) return Toast.info('请输入裁判口令')

        setLoading(true)
        try {
            const res = await request.get('/v2/sports/schedules/', {
                params: { referee_code: targetCode, page_size: 100 }
            })

            const items = res.items || []
            setSchedules(items)
            if (items.length === 0) {
                let closedOnly = false
                try {
                    const probe = await request.get('/v2/sports/schedules/', {
                        params: { referee_code: targetCode, page_size: 1, include_closed: 1 }
                    })
                    closedOnly = (probe.items || []).length > 0
                } catch (err) {
                    /* probe ignore */
                }
                Toast.fail(closedOnly ? '该口令只对应已结束的运动会' : '口令无效或暂无比赛安排')
            } else {
                Toast.success(`成功加载 ${items.length} 个任务`)
            }
        } catch (e) {
            console.error('Verify error:', e)
            Toast.fail('认证失败')
        } finally {
            setLoading(false)
        }
    }

    const selectSchedule = (id) => {
        navigate(`/sports/referee/workbench?code=${code}&scheduleId=${id}`)
    }

    const pendingSchedules = schedules.filter(s => s.status !== 'completed')
    const completedSchedules = schedules.filter(s => s.status === 'completed')

    const renderScheduleCard = (s) => (
        <div 
            key={s.id} 
            onClick={() => selectSchedule(s.id)}
            style={{ 
                background: '#ffffff',
                borderRadius: 16,
                padding: '16px',
                marginBottom: 12,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: 'var(--shadow-card, 0 4px 16px rgba(15, 23, 42, 0.05))',
                border: '1px solid #f1f5f9',
                cursor: 'pointer'
            }}
        >
            <div style={{ flex: 1, minWidth: 0, marginRight: 12 }}>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                    {s.event_name}
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                    {s.round_name} · {s.group_name}
                </div>
                <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#64748b' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <ClockO style={{ marginRight: 4, color: '#056b4f' }} /> 
                        {s.start_time ? s.start_time.substring(11, 16) : '待定'}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <LocationO style={{ marginRight: 4, color: '#056b4f' }} /> 
                        {s.venue || '跑道'}
                    </div>
                </div>
            </div>
            <div style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Tag
                    round
                    style={{
                        background: s.status === 'completed' ? '#f1f5f9' : '#f0fdf4',
                        color: s.status === 'completed' ? '#64748b' : '#056b4f',
                        border: 'none',
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '2px 8px',
                        marginBottom: 6
                    }}
                >
                    {s.status === 'completed' ? '已完成' : '录入中'}
                </Tag>
                <Arrow style={{ fontSize: 14, color: '#94a3b8' }} />
            </div>
        </div>
    )

    return (
        <div className="referee-login-page" style={{ background: 'var(--canvas-bg, #f8fafc)', minHeight: '100vh', paddingBottom: 40 }}>
            {/* 沉浸式星瀚空天蓝页眉 */}
            <div style={{
                background: 'linear-gradient(135deg, #055e43 0%, #076f51 50%, #0c8c68 100%)',
                padding: '44px 16px 20px',
                color: '#fff',
                position: 'relative',
                overflow: 'hidden'
            }}>
                {/* 装饰半透圆 */}
                <div style={{
                    position: 'absolute', top: -30, right: -30,
                    width: 140, height: 140, borderRadius: '50%',
                    background: 'rgba(255, 255, 255, 0.08)'
                }} />

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
                    <div
                        onClick={() => navigate(-1)}
                        style={{
                            width: 36, height: 36, borderRadius: '50%',
                            background: 'rgba(255,255,255,0.2)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        <ArrowLeft color="#fff" fontSize={18} />
                    </div>

                    <div style={{ textAlign: 'center' }}>
                        <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#fff', letterSpacing: '-0.2px' }}>
                            裁判工作台
                        </h1>
                        <p style={{ margin: '2px 0 0', fontSize: 11.5, color: 'rgba(255,255,255,0.85)', fontWeight: 400 }}>
                            运动会执裁 · 口令核验与成绩上报
                        </p>
                    </div>

                    <div style={{ width: 36 }} />
                </div>
            </div>

            {/* 口令验证输入卡片 */}
            <div style={{ padding: '14px 16px' }}>
                <div style={{
                    background: '#ffffff',
                    borderRadius: 16,
                    padding: '12px 14px',
                    boxShadow: 'var(--shadow-card, 0 4px 16px rgba(15, 23, 42, 0.05))',
                    border: '1px solid #e2e8f0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10
                }}>
                    <div style={{
                        flex: 1,
                        background: '#f8fafc',
                        borderRadius: 12,
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 12px',
                        border: '1px solid #e2e8f0'
                    }}>
                        <Scan style={{ color: '#056b4f', marginRight: 8, fontSize: 18 }} />
                        <Field
                            value={code}
                            onChange={setCode}
                            placeholder="请输入裁判口令 (如: T888)"
                            border={false}
                            style={{ background: 'transparent', padding: '8px 0', fontSize: 14 }}
                        />
                    </div>
                    <Button 
                        round 
                        size="small" 
                        style={{
                            padding: '0 20px',
                            height: 38,
                            background: '#056b4f',
                            borderColor: '#056b4f',
                            color: '#ffffff',
                            fontWeight: 700,
                            boxShadow: '0 4px 12px rgba(5, 107, 79, 0.25)'
                        }}
                        loading={loading}
                        onClick={() => handleVerify()}
                    >
                        进入
                    </Button>
                </div>
            </div>

            {/* 任务状态选项卡 */}
            <div style={{ background: '#ffffff', borderBottom: '1px solid #e2e8f0' }}>
                <Tabs
                    active={activeTab}
                    onChange={setActiveTab}
                    color="#056b4f"
                    titleActiveColor="#056b4f"
                    border={false}
                    lineHeight={3}
                    lineWidth={32}
                >
                    <Tabs.TabPane title={`待办任务 (${pendingSchedules.length})`} />
                    <Tabs.TabPane title={`已完成 (${completedSchedules.length})`} />
                </Tabs>
            </div>

            <div style={{ padding: '14px 16px' }}>
                {activeTab === 0 ? (
                    loading ? (
                        <div style={{ textAlign: 'center', padding: '60px 0' }}>
                            <Loading type="spinner" color="#056b4f" vertical>加载执裁任务中...</Loading>
                        </div>
                    ) : pendingSchedules.length > 0 ? (
                        pendingSchedules.map(renderScheduleCard)
                    ) : (
                        <div style={{ padding: '60px 0' }}>
                            <Empty description={schedules.length === 0 ? "请输入口令获取任务" : "暂无待办任务"} />
                        </div>
                    )
                ) : (
                    loading ? (
                        <div style={{ textAlign: 'center', padding: '60px 0' }}>
                            <Loading type="spinner" color="#056b4f" vertical>加载任务中...</Loading>
                        </div>
                    ) : completedSchedules.length > 0 ? (
                        completedSchedules.map(renderScheduleCard)
                    ) : (
                        <div style={{ padding: '60px 0' }}>
                            <Empty description="暂无已完成任务" />
                        </div>
                    )
                )}
            </div>
        </div>
    )
}
